import {CustomerReferenceError} from "@sakura-grp/database/errors";
import {
  formatDate,
  formatDateTime,
  ACTIVITY_TYPE_MAP,
  STORE_SHORT_NAMES,
  CUSTOMER_TYPE_EMOJI,
  getDayOfWeek,
  type DashboardCustomerType,
} from '@sakura-grp/lib';
import {normalizeName} from '@/lib/normalize-name';
import {normalizePostalCodeInput} from '@/lib/api/business-cards';
import type {
  CustomerDetailData,
  StoreInfo,
  Announcement,
  Reservation,
  BusinessCard,
  BranchCustomer,
  ActivityType,
  ReservationType,
  SalesStatus,
  CustomerType as DetailCustomerType, // 詳細画面用の顧客タイプ
  CustomerListItem,
} from '~/types/customer-detail.types';
import { getBrowserClient } from '@/lib/supabase';
import { getStoreLogoUrl, getHostessProfileImageUrlOrPlaceholder } from '@/lib/storage-utils';
import {
  ACTIVITY_THRESHOLDS,
  calculateVisitStatus,
  resolveCustomerCompanies,
} from '@sakura-grp/database/services';

// ========================================
// Supabaseクエリ結果の型定義（型安全性向上）
// ========================================

/** 店舗情報 */
interface StoreRow {
  id: string;
  store_name: string;
  logo_path: string | null;
}

/** ホステス情報（リレーション含む） */
interface HostessWithStore {
  id: string;
  name: string;
  current_assignment: { store_id: string; store: StoreRow | null }[] | null;
}

/** 顧客基本情報 */
interface CustomerBasic {
  id: string;
  name: string | null;
  company_name: string | null;
  registration_date: string | null;
  allow_phone: boolean | null;
  allow_message: boolean | null;
  allow_dm: boolean | null;
  allow_visit: boolean | null;
}

/** hostess_customersクエリ結果 */
interface HostessCustomerRow {
  customer_id: string;
  store_id: string;
  assigned_at: string | null;
  customer: CustomerBasic | null;
  hostess: HostessWithStore | null;
}

/** store_customersクエリ結果 */
interface StoreCustomerRow {
  customer_id: string;
  assigned_at: string;
  store_id: string;
  notes: string | null;
  customer: CustomerBasic | null;
  store: StoreRow | null;
}

/** customer_referralsクエリ結果 */
interface CustomerReferralRow {
  id: string;
  store_id: string;
  referred_id: string;
  created_at: string;
  referred: {
    id: string;
    name: string | null;
    company_name: string | null;
    allow_phone?: boolean | null;
    allow_message?: boolean | null;
    allow_dm?: boolean | null;
    allow_visit?: boolean | null;
  } | null;
  referrer: { id: string; name: string | null } | null;
  store: StoreRow | null;
}

/** activitiesクエリ結果 */
interface ActivityRow {
  id: string;
  activity_date: string;
  activity_type: string;
  store_id: string;
  hostess_id: string;
  store: StoreRow | null;
  hostess: { id: string; name: string | null; status: string | null; profile_image_path: string | null } | null;
}

/** bookingsクエリ結果 */
interface BookingRow {
  id: string;
  booking_date: string;
  reserved_guest_count: string | null;
  status: string | null;
  store: StoreRow | null;
  booking_hostesses: { hostess_id: string; cancelled_at: string | null }[];
}

/** hostess_customersの詳細クエリ結果 */
interface HostessCustomerDetailRow {
  hostess_id: string;
  assigned_at: string | null;
  hostess: {
    id: string;
    name: string | null;
    status: string | null;
    profile_image_path: string | null;
    current_assignment: { store_id: string; store: StoreRow | null }[] | null;
  } | null;
}

/** store_customersの詳細クエリ結果 */
interface StoreCustomerDetailRow {
  store_id: string;
  assigned_at: string;
  notes: string | null;
  store: StoreRow | null;
}

/** keep_bottlesクエリ結果 */
interface KeepBottleRow {
  id: string;
  bottle_name_override: string | null;
  store_id: string;
  status: string | null;
  bottle: { id: string; name: string } | null;
  store: { store_name: string; logo_path: string | null } | null;
}

/** 枝客referralクエリ結果 */
interface BranchReferralRow {
  id: string;
  store_id: string;
  referred: {
    id: string;
    name: string | null;
    name_kana: string | null;
    company_name: string | null;
    mobile_phone: string | null;
    allow_phone: boolean | null;
    allow_message: boolean | null;
    allow_dm: boolean | null;
    allow_visit: boolean | null;
  } | null;
}

/** 親元referralクエリ結果 */
interface ParentReferralRow {
  id: string;
  store_id: string;
  referrer: { id: string; name: string | null } | null;
}

/** related_customers_summaryクエリ結果 */
interface RelatedCustomerRow {
  customer_id: string;
  relation_type: string;
  last_relation_date: string | null;
  attend_count: number | null;
}

/** 名刺クエリ結果 */
interface BusinessCardRow {
  id: string;
  name_kanji: string | null;
  name_furigana: string | null;
  mobile_number: string | null;
  email_address: string | null;
  company_name: string | null;
  position: string | null;
  company_phone: string | null;
  fax: string | null;
  address: string | null;
  postal_code: string | null;
  file: string | null;
  file_back: string | null;
  handwritten_note: string | null;
}

/** 顧客簡易情報 */
interface CustomerSimpleRow {
  id: string;
  name: string | null;
  company_name: string | null;
  registration_date: string | null;
  allow_phone: boolean | null;
  allow_message: boolean | null;
  allow_dm: boolean | null;
  allow_visit: boolean | null;
}


/**
 * 自分関連の顧客IDを取得する
 * 以下のいずれかに該当する顧客を「自分関連」とみなす:
 * 1. 自分がもらった名刺に紐付く顧客
 * 2. 自分の担当顧客
 * 3. 担当顧客の親元（紹介者）
 * 4. 担当顧客の紹介客
 *
 * @param hostessId - ログイン中のホステスID
 * @returns 自分関連の顧客IDのSet
 */
export async function fetchMyRelatedCustomerIds(
  hostessId: string
): Promise<Set<string>> {
  const supabase = getBrowserClient();

  // 並列実行: 名刺経由 + 担当顧客
  const [bizCardsRes, myCustomersRes] = await Promise.all([
    supabase
      .from('business_cards')
      .select('customer_id')
      .eq('hostess_id', hostessId)
      .not('customer_id', 'is', null),
    supabase
      .from('hostess_customers')
      .select('customer_id')
      .eq('hostess_id', hostessId),
  ]);

  const relatedIds = new Set<string>();
  const myCustomerIds: string[] = [];

  // 名刺経由の顧客ID
  for (const bc of bizCardsRes.data || []) {
    if (bc.customer_id) relatedIds.add(bc.customer_id);
  }

  // 担当顧客ID
  for (const hc of myCustomersRes.data || []) {
    relatedIds.add(hc.customer_id);
    myCustomerIds.push(hc.customer_id);
  }

  // 紹介関係（担当顧客がある場合のみ）
  if (myCustomerIds.length > 0) {
    const [parentRes, childRes] = await Promise.all([
      // 担当顧客の親元（紹介者）
      supabase
        .from('customer_referrals')
        .select('referrer_id')
        .in('referred_id', myCustomerIds),
      // 担当顧客の紹介客
      supabase
        .from('customer_referrals')
        .select('referred_id')
        .in('referrer_id', myCustomerIds),
    ]);

    for (const ref of parentRes.data || []) {
      relatedIds.add(ref.referrer_id);
    }
    for (const ref of childRes.data || []) {
      relatedIds.add(ref.referred_id);
    }
  }

  return relatedIds;
}

// 複数顧客の営業活動統計を一括取得（N+1クエリ問題の解消）
type ActivityStatsResult = {
  phone: number;
  message: number;
  note: number;
  visit: number;
  statusType: 'good' | 'poor';
  statusLabel: string;
};

/**
 * 複数顧客の営業活動統計を一括取得
 * - 全期間（30日制限なし）
 * - isRelatedToMe フィルター適用（自分の活動 OR 未担当枝客への活動）
 * - limit 50（顧客詳細の営業履歴タブと同じ）
 */
async function getBatchActivityStats(
  customerIds: string[],
  options: { storeId?: string; hostessId?: string } = {}
): Promise<Map<string, ActivityStatsResult>> {
  if (customerIds.length === 0) {
    return new Map();
  }

  const { storeId, hostessId } = options;
  const supabase = getBrowserClient();

  // isRelatedToMe 判定用データを取得
  let myCustomerIds = new Set<string>();
  let myReferredUnassignedCustomerIds = new Set<string>();

  if (hostessId) {
    // 1. 自分の担当顧客IDを取得（relationship_type = 'assigned' のみ）
    const { data: hcData } = await supabase
      .from('hostess_customers')
      .select('customer_id')
      .eq('hostess_id', hostessId)
      .eq('relationship_type', 'assigned');
    myCustomerIds = new Set((hcData || []).map((c) => c.customer_id));

    // 2. 担当顧客が紹介した枝客のうち、まだ担当がついていない顧客を取得
    if (myCustomerIds.size > 0) {
      const { data: referralsData } = await supabase
        .from('customer_referrals')
        .select('referred_id')
        .in('referrer_id', Array.from(myCustomerIds));
      const myReferredIds = new Set((referralsData || []).map((r) => r.referred_id));

      // 全担当顧客一覧（担当有無判定用、relationship_type = 'assigned' のみ）
      const { data: allAssignedData } = await supabase
        .from('hostess_customers')
        .select('customer_id')
        .eq('relationship_type', 'assigned');
      const allAssignedIds = new Set((allAssignedData || []).map((c) => c.customer_id));

      // 未担当の枝客のみを抽出
      for (const refId of myReferredIds) {
        if (!allAssignedIds.has(refId)) {
          myReferredUnassignedCustomerIds.add(refId);
        }
      }
    }
  }

  // 営業活動を取得（全期間、limit 50 per customer相当）
  let query = supabase
    .from('activities')
    .select('id, customer_id, activity_type, hostess_id')
    .in('customer_id', customerIds)
    .is('cancelled_at', null)
    .order('activity_date', { ascending: false });

  if (storeId) {
    query = query.eq('store_id', storeId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[getBatchActivityStats] Error:', error);
    return new Map();
  }

  // 顧客ごとに活動をカウント（isRelatedToMe フィルター適用、最大50件まで）
  const countsMap = new Map<string, { phone: number; message: number; letter: number; visit: number; count: number }>();

  for (const a of data || []) {
    // isRelatedToMe 判定
    const isMyActivity = hostessId ? a.hostess_id === hostessId : true;
    const isUnassignedBranch = myReferredUnassignedCustomerIds.has(a.customer_id);
    const isRelatedToMe = hostessId ? isMyActivity || isUnassignedBranch : true;

    if (!isRelatedToMe) continue;

    if (!countsMap.has(a.customer_id)) {
      countsMap.set(a.customer_id, { phone: 0, message: 0, letter: 0, visit: 0, count: 0 });
    }
    const counts = countsMap.get(a.customer_id)!;

    // limit 50 per customer
    if (counts.count >= 50) continue;
    counts.count++;

    switch (a.activity_type) {
      case 'phone':
        counts.phone++;
        break;
      case 'message':
        counts.message++;
        break;
      case 'letter':
        counts.letter++;
        break;
      case 'visit':
        counts.visit++;
        break;
    }
  }

  // 結果を構築
  const statusLabelMap: Record<'good' | 'poor', string> = {
    good: '営業良好',
    poor: '営業不足',
  };

  const statsMap = new Map<string, ActivityStatsResult>();
  for (const customerId of customerIds) {
    const counts = countsMap.get(customerId) || { phone: 0, message: 0, letter: 0, visit: 0, count: 0 };
    const total = counts.phone + counts.message + counts.letter + counts.visit;
    const statusType = ACTIVITY_THRESHOLDS.evaluate(total);
    statsMap.set(customerId, {
      phone: counts.phone,
      message: counts.message,
      note: counts.letter,
      visit: counts.visit,
      statusType,
      statusLabel: statusLabelMap[statusType],
    });
  }

  return statsMap;
}


// 顧客データの中間型
interface CustomerRawData {
  id: string;
  type: DashboardCustomerType;
  assignedAt: string | null;
  storeName: string;
  storeId?: string;
  hostessId?: string;
  hostessName?: string;
  name: string;
  company: string;
  // NG制限フラグ（false = NG）
  allowPhone?: boolean | null;
  allowMessage?: boolean | null;
  allowDm?: boolean | null;
  allowVisit?: boolean | null;
}

/**
 * 顧客一覧を取得する（ダッシュボード用）
 * @param type - 顧客タイプでフィルタリング（オプション）
 * @param hostessId - ログイン中のホステスID
 * @param storeIds - ホステスの所属店舗ID一覧（過去+現在）
 * @returns 顧客一覧
 */
export async function fetchCustomers(
  type?: DashboardCustomerType,
  hostessId?: string,
  storeIds?: string[]
): Promise<CustomerListItem[]> {
  const supabase = getBrowserClient();

  // hostessIdがない場合は空配列を返す（本番では必ず存在するはず）
  if (!hostessId) {
    console.warn("[fetchCustomers] hostessId is required but not provided");
    return [];
  }

  // 店舗IDセットを作成（高速検索用）
  const storeIdSet = new Set(storeIds || []);

  try {
    // 顧客の生データを収集（統計取得前）
    const rawCustomers: CustomerRawData[] = [];

    // 担当顧客 (client): hostess_customers.relationship_type = 'assigned' で判定
    if (!type || type === 'assigned') {
      const { data: assignedCustomers } = await supabase
        .from('hostess_customers')
        .select(`
          customer_id,
          store_id,
          assigned_at,
          relationship_type,
          customer:customers(
            id,
            name,
            company_name,
            registration_date,
            allow_phone,
            allow_message,
            allow_dm,
            allow_visit
          ),
          hostess:hostesses(
            id,
            name,
            current_assignment:hostess_store_assignments(
              store_id,
              store:stores(id, store_name, logo_path)
            )
          )
        `)
        .eq('hostess_id', hostessId)
        .eq('relationship_type', 'assigned');

      if (assignedCustomers) {
        for (const hc of assignedCustomers as HostessCustomerRow[]) {
          if (!hc.customer) continue;

          const currentAssignment = hc.hostess?.current_assignment?.[0];
          rawCustomers.push({
            id: hc.customer.id,
            type: 'assigned',
            assignedAt: hc.assigned_at,
            storeName: currentAssignment?.store?.store_name || '不明',
            storeId: currentAssignment?.store_id,
            hostessId: hostessId,
            hostessName: hc.hostess?.name,
            name: hc.customer.name || '不明',
            company: hc.customer.company_name || '',
            allowPhone: hc.customer.allow_phone,
            allowMessage: hc.customer.allow_message,
            allowDm: hc.customer.allow_dm,
            allowVisit: hc.customer.allow_visit,
          });
        }
      }
    }

    // 他の担当 (others): 所属店舗の他ホステスが担当（relationship_type = 'assigned'）
    if (!type || type === 'assigned') {
      const { data: othersCustomers } = await supabase
        .from('hostess_customers')
        .select(`
          customer_id,
          store_id,
          assigned_at,
          relationship_type,
          customer:customers(
            id,
            name,
            company_name,
            registration_date,
            allow_phone,
            allow_message,
            allow_dm,
            allow_visit
          ),
          hostess:hostesses(
            id,
            name,
            current_assignment:hostess_store_assignments(
              store_id,
              store:stores(id, store_name, logo_path)
            )
          )
        `)
        .neq('hostess_id', hostessId)
        .eq('relationship_type', 'assigned');

      if (othersCustomers && storeIdSet.size > 0) {
        for (const hc of othersCustomers as HostessCustomerRow[]) {
          if (!hc.customer) continue;

          const currentAssignment = hc.hostess?.current_assignment?.[0];
          // 所属店舗（過去+現在）に含まれる店舗の顧客のみ
          if (!currentAssignment?.store_id || !storeIdSet.has(currentAssignment.store_id)) continue;
          rawCustomers.push({
            id: hc.customer.id,
            type: 'assigned',
            assignedAt: hc.assigned_at,
            storeName: currentAssignment?.store?.store_name || '不明',
            storeId: currentAssignment?.store_id,
            hostessId: hc.hostess?.id,
            hostessName: hc.hostess?.name,
            name: hc.customer.name || '不明',
            company: hc.customer.company_name || '',
            allowPhone: hc.customer.allow_phone,
            allowMessage: hc.customer.allow_message,
            allowDm: hc.customer.allow_dm,
            allowVisit: hc.customer.allow_visit,
          });
        }
      }
    }

    // 店客 (store): store_customers.category = 'store'（所属店舗全て）
    if ((!type || type === 'store') && storeIdSet.size > 0) {
      const { data: storeCustomers } = await supabase
        .from('store_customers')
        .select(`
          customer_id,
          assigned_at,
          store_id,
          customer:customers(
            id,
            name,
            company_name,
            registration_date,
            allow_phone,
            allow_message,
            allow_dm,
            allow_visit
          ),
          store:stores(id, store_name, logo_path)
        `)
        .in('store_id', Array.from(storeIdSet))
        .eq('category', 'store')
        .is('deleted_at', null);

      if (storeCustomers) {
        for (const sc of storeCustomers as StoreCustomerRow[]) {
          if (!sc.customer) continue;
          rawCustomers.push({
            id: sc.customer.id,
            type: 'store',
            assignedAt: sc.assigned_at,
            storeName: sc.store?.store_name || '不明',
            storeId: sc.store_id,
            name: sc.customer.name || '不明',
            company: sc.customer.company_name || '',
            allowPhone: sc.customer.allow_phone,
            allowMessage: sc.customer.allow_message,
            allowDm: sc.customer.allow_dm,
            allowVisit: sc.customer.allow_visit,
          });
        }
      }
    }

    // 非会員 (non): store_customers.category = 'non'（所属店舗全て）
    if ((!type || type === 'non') && storeIdSet.size > 0) {
      const { data: nonCustomers } = await supabase
        .from('store_customers')
        .select(`
          customer_id,
          assigned_at,
          store_id,
          customer:customers(
            id,
            name,
            company_name,
            registration_date,
            allow_phone,
            allow_message,
            allow_dm,
            allow_visit
          ),
          store:stores(id, store_name, logo_path)
        `)
        .in('store_id', Array.from(storeIdSet))
        .eq('category', 'non')
        .is('deleted_at', null);

      if (nonCustomers) {
        for (const sc of nonCustomers as StoreCustomerRow[]) {
          if (!sc.customer) continue;
          rawCustomers.push({
            id: sc.customer.id,
            type: 'non',
            assignedAt: sc.assigned_at,
            storeName: sc.store?.store_name || '不明',
            storeId: sc.store_id,
            name: sc.customer.name || '不明',
            company: sc.customer.company_name || '',
            allowPhone: sc.customer.allow_phone,
            allowMessage: sc.customer.allow_message,
            allowDm: sc.customer.allow_dm,
            allowVisit: sc.customer.allow_visit,
          });
        }
      }
    }

    // 全顧客の活動統計を一括取得（N+1クエリ問題の解消）
    // 複数店舗の場合は全店舗の活動を集計
    const customerIds = rawCustomers.map(c => c.id);
    const activityStatsMap = await getBatchActivityStats(customerIds, { hostessId });

    // 会社名はホステス視点で代表名刺ベースに動的解決
    // 仕様: docs/domain/20260527-customer-company-context-display.md
    const companyMap = await resolveCustomerCompanies(supabase, customerIds, hostessId);
    for (const raw of rawCustomers) {
      raw.company = companyMap.get(raw.id)?.companyName ?? '';
    }

    // 最終来店日を一括取得（completed の最新 booking_date）
    const lastVisitMap = new Map<string, string>();
    if (customerIds.length > 0) {
      const { data: lastVisits } = await supabase
        .from('bookings')
        .select('customer_id, booking_date')
        .in('customer_id', customerIds)
        .eq('status', 'completed')
        .order('booking_date', { ascending: false });

      for (const row of lastVisits || []) {
        if (row.customer_id && row.booking_date && !lastVisitMap.has(row.customer_id)) {
          lastVisitMap.set(row.customer_id, row.booking_date);
        }
      }
    }

    // 最終的な顧客データを構築（ソートはダッシュボード側でタブ別に適用）
    const customers: CustomerListItem[] = rawCustomers.map(raw => {
      const stats = activityStatsMap.get(raw.id) || {
        phone: 0,
        message: 0,
        note: 0,
        visit: 0,
        statusType: 'poor' as const,
        statusLabel: '営業不足',
      };

      return {
        id: raw.id,
        type: raw.type,
        registeredDate: formatDate(raw.assignedAt),
        lastVisitDate: formatDate(lastVisitMap.get(raw.id) || null),
        storeName: raw.storeName,
        storeId: raw.storeId,
        hostessId: raw.hostessId,
        hostessName: raw.hostessName,
        statusType: stats.statusType,
        statusLabel: stats.statusLabel,
        activities: {
          phone: stats.phone,
          message: stats.message,
          note: stats.note,
          visit: stats.visit,
        },
        restrictions: {
          phone: raw.allowPhone === false,
          message: raw.allowMessage === false,
          dm: raw.allowDm === false,
          visit: raw.allowVisit === false,
        },
        emoji: CUSTOMER_TYPE_EMOJI[raw.type],
        name: raw.name,
        company: raw.company,
        linkTo: `/customers/${raw.id}`,
      };
    });

    return customers;
  } catch (error) {
    console.error('Failed to fetch customers:', error);
    throw error instanceof Error ? error : new Error('顧客一覧の取得に失敗しました');
  }
}





// 営業ステータスを計算
function calculateSalesStatus(activityCounts: { phone: number; message: number; letter: number; visit: number }): SalesStatus {
  const total = activityCounts.phone + activityCounts.message + activityCounts.letter + activityCounts.visit;
  return ACTIVITY_THRESHOLDS.evaluate(total);
}

// calculateVisitStatus は @sakura-grp/database/services から import

/**
 * 顧客詳細を取得する
 * @param id - 顧客ID
 * @param type - 顧客タイプ
 * @param hostessId - ログイン中のホステスID（営業履歴のフィルタリング用）
 * @returns 顧客詳細データ
 */
export async function fetchCustomerDetail(
  id: string,
  type: DetailCustomerType,
  hostessId?: string
): Promise<CustomerDetailData> {
  const supabase = getBrowserClient();

  try {
    // 1. 基本情報取得
    const { data: customer, error: customerError } = await supabase
      .from('customers')
      .select(`
        id, name, name_kana,
        mobile_phone, email, postal_code, address_full,
        created_at, memo,
        allow_dm, allow_phone, allow_message, allow_visit
      `)
      .eq('id', id)
      .single();

    if (customerError || !customer) {
      console.error('[fetchCustomerDetail] 顧客取得エラー:', customerError);
      throw new Error('顧客が見つかりません');
    }

    // 2. 担当ホステス情報取得（relationship_type = 'assigned' のみ）
    const { data: hostessCustomers } = await supabase
      .from('hostess_customers')
      .select(`
        hostess_id,
        assigned_at,
        relationship_type,
        hostess:hostesses(
          id, name, status, profile_image_path,
          current_assignment:hostess_store_assignments(
            store_id,
            store:stores(id, store_name, logo_path)
          )
        )
      `)
      .eq('customer_id', id)
      .eq('relationship_type', 'assigned');

    // 2b. ホステスメモ取得（担当関係とは独立）
    const memoData = hostessId
      ? (await supabase
          .from('hostess_customer_memos')
          .select('notes')
          .eq('customer_id', id)
          .eq('hostess_id', hostessId)
          .maybeSingle()
        ).data
      : null;

    // 3. 店舗情報取得 (store_customers)
    const { data: storeCustomers } = await supabase
      .from('store_customers')
      .select(`
        store_id,
        assigned_at,
        notes,
        store:stores(id, store_name, logo_path)
      `)
      .eq('customer_id', id)
      .is('deleted_at', null);

    // 4. キープボトル取得
    const { data: keepBottles } = await supabase
      .from('keep_bottles')
      .select(`
        id, bottle_name_override, store_id, status,
        bottle:bottles(id, name),
        store:stores(store_name, logo_path)
      `)
      .eq('customer_id', id)
      .eq('status', 'kept')
      // 論理削除されたキープは出さない（ADR 20260817）
      .is('deleted_at', null);

    // 5. 名刺情報取得
    const { data: businessCards } = await supabase
      .from('business_cards')
      .select('*')
      .eq('customer_id', id);

    // 6. 予約履歴取得
    const { data: bookings } = await supabase
      .from('bookings')
      .select(`
        id, booking_date, reserved_guest_count, status,
        store:stores(id, store_name, logo_path),
        booking_hostesses!left(hostess_id, cancelled_at)
      `)
      .eq('customer_id', id)
      .order('booking_date', { ascending: false })
      .limit(20);

    // 7. 営業活動取得
    const { data: activities } = await supabase
      .from('activities')
      .select(`
        id, activity_date, activity_type, store_id, hostess_id,
        store:stores(id, store_name, logo_path),
        hostess:hostesses!activities_hostess_id_fkey(id, name, status, profile_image_path)
      `)
      .eq('customer_id', id)
      .is('cancelled_at', null)
      .order('activity_date', { ascending: false })
      .limit(50);

    // 8. 枝客取得（担当顧客の場合のみ）- N+1クエリ解消版
    let branches: BranchCustomer[] = [];
    if (type === 'assigned') {
      const { data: referrals } = await supabase
        .from('customer_referrals')
        .select(`
          id,
          store_id,
          referred:customers!referred_id(
            id, name, name_kana, company_name, mobile_phone,
            allow_phone, allow_message, allow_dm, allow_visit
          )
        `)
        .eq('referrer_id', id);

      if (referrals && referrals.length > 0) {
        // 複数店舗分の紹介レコードを referred_id で重複排除しつつ store_id を集約
        const branchMap = new Map<string, { ref: BranchReferralRow; storeIds: string[] }>();
        for (const ref of referrals as BranchReferralRow[]) {
          if (!ref.referred) continue;
          const existing = branchMap.get(ref.referred.id);
          if (existing) {
            if (!existing.storeIds.includes(ref.store_id)) {
              existing.storeIds.push(ref.store_id);
            }
          } else {
            branchMap.set(ref.referred.id, { ref, storeIds: [ref.store_id] });
          }
        }

        // 枝客IDを収集して一括で営業統計を取得（N+1クエリ解消）
        const branchIds = Array.from(branchMap.keys());
        const branchStatsMap = await getBatchActivityStats(branchIds, { hostessId });

        for (const [, { ref, storeIds }] of branchMap) {
          const stats = branchStatsMap.get(ref.referred!.id) || {
            phone: 0,
            message: 0,
            note: 0,
            visit: 0,
            statusType: 'poor' as const,
            statusLabel: '営業不足',
          };
          branches.push({
            id: ref.referred!.id,
            name: ref.referred!.name || '',
            kana: ref.referred!.name_kana || '',
            company: ref.referred!.company_name || '',
            mobilePhone: ref.referred!.mobile_phone || '',
            storeIds,
            points: 0, // ポイント機能は後回し
            latestVisit: '', // 後で実装
            salesStatus: stats.statusType,
            restrictions: {
              phone: ref.referred!.allow_phone === false,
              message: ref.referred!.allow_message === false,
              dm: ref.referred!.allow_dm === false,
              visit: ref.referred!.allow_visit === false,
            },
            activityCounts: {
              phone: stats.phone,
              message: stats.message,
              letter: stats.note,
              visit: stats.visit,
            },
          });
        }
      }
    }

    // 9. 親元情報取得（この顧客を紹介した人）
    const { data: parentReferrals } = await supabase
      .from('customer_referrals')
      .select(`
        id,
        store_id,
        referrer:customers!referrer_id(
          id, name
        )
      `)
      .eq('referred_id', id);

    // データ変換
    // 店舗ごとの情報を構築
    const storeMap = new Map<string, StoreInfo>();

    // hostess_customersから店舗情報を追加
    if (hostessCustomers) {
      for (const hc of hostessCustomers as HostessCustomerDetailRow[]) {
        const currentAssignment = hc.hostess?.current_assignment?.[0];
        if (!currentAssignment?.store_id) continue;
        const storeId = currentAssignment.store_id;
        if (!storeMap.has(storeId)) {
          storeMap.set(storeId, {
            storeId: storeId,
            storeName: currentAssignment.store?.store_name || '不明',
            storeLogo: getStoreLogoUrl(currentAssignment.store?.logo_path),
            storeShortName: STORE_SHORT_NAMES[storeId] || '店',
            hostesses: [],
            bottles: [],
            parents: [],
          });
        }
        const store = storeMap.get(storeId)!;
        store.hostesses.push({
          id: hc.hostess!.id,
          name: hc.hostess!.name || '',
          avatar: getHostessProfileImageUrlOrPlaceholder(hc.hostess!.profile_image_path),
          status: hc.hostess!.status === 'active' ? 'working' : 'absent',
        });
      }
    }

    // store_customersから店舗情報を追加
    if (storeCustomers) {
      for (const sc of storeCustomers as StoreCustomerDetailRow[]) {
        if (!sc.store_id) continue;
        const storeId = sc.store_id;
        if (!storeMap.has(storeId)) {
          storeMap.set(storeId, {
            storeId: storeId,
            storeName: sc.store?.store_name || '不明',
            storeLogo: getStoreLogoUrl(sc.store?.logo_path),
            storeShortName: STORE_SHORT_NAMES[storeId] || '店',
            hostesses: [],
            bottles: [],
            parents: [],
          });
        }
      }
    }

    // activitiesから店舗情報を追加（営業活動がある店舗も表示対象に）
    if (activities) {
      for (const a of activities as ActivityRow[]) {
        if (!a.store_id) continue;
        const storeId = a.store_id;
        if (!storeMap.has(storeId)) {
          storeMap.set(storeId, {
            storeId: storeId,
            storeName: a.store?.store_name || '不明',
            storeLogo: getStoreLogoUrl(a.store?.logo_path),
            storeShortName: STORE_SHORT_NAMES[storeId] || '店',
            hostesses: [],
            bottles: [],
            parents: [],
          });
        }
      }
    }

    // ボトル情報を追加（ボトルの店舗がstoreMapに未登録の場合は追加）
    if (keepBottles) {
      for (const kb of keepBottles as KeepBottleRow[]) {
        if (!kb.store_id) continue;
        if (!storeMap.has(kb.store_id)) {
          storeMap.set(kb.store_id, {
            storeId: kb.store_id,
            storeName: kb.store?.store_name || '不明',
            storeLogo: getStoreLogoUrl(kb.store?.logo_path),
            storeShortName: STORE_SHORT_NAMES[kb.store_id] || '店',
            hostesses: [],
            bottles: [],
            parents: [],
          });
        }
        const store = storeMap.get(kb.store_id)!;
        store.bottles.push({
          id: kb.id,
          name: kb.bottle_name_override || kb.bottle?.name || '不明',
        });
      }
    }

    // 親元情報を追加
    if (parentReferrals) {
      for (const pr of parentReferrals as ParentReferralRow[]) {
        if (!pr.store_id || !storeMap.has(pr.store_id)) continue;
        const store = storeMap.get(pr.store_id)!;
        store.parents.push({
          customerId: pr.referrer?.id || '',
          customerName: pr.referrer?.name || '不明',
          hostessId: '', // 紹介者の担当ホステスは別途取得が必要
          hostessName: '',
          hostessEmoji: '💁‍♀️',
        });
      }
    }

    // 営業活動カウント
    const activityCounts = {
      phone: 0,
      message: 0,
      letter: 0,
      visit: 0,
    };
    if (activities) {
      for (const a of activities as ActivityRow[]) {
        switch (a.activity_type) {
          case 'phone': activityCounts.phone++; break;
          case 'message': activityCounts.message++; break;
          case 'letter': activityCounts.letter++; break;
          case 'visit': activityCounts.visit++; break;
        }
      }
    }

    // 予約統計
    const stats = {
      dohan: 0,
      kakuyaku: 0,
      yoyaku: 0,
    };
    if (bookings) {
      for (const b of bookings as BookingRow[]) {
        // booking_hostessesにhostess_idがあれば同伴（キャンセルされていないもののみ）
        const hasHostess = b.booking_hostesses?.some((bh) => bh.hostess_id && !bh.cancelled_at);
        if (hasHostess) {
          stats.dohan++;
        } else if (b.status === 'confirmed') {
          stats.kakuyaku++;
        } else {
          stats.yoyaku++;
        }
      }
    }

    // 最終来店日を取得（完了ステータスの予約のみ — 専用クエリで20件制限を回避）
    const {data: lastVisitRow} = await supabase
      .from('bookings')
      .select('booking_date')
      .eq('customer_id', id)
      .eq('status', 'completed')
      .order('booking_date', {ascending: false})
      .limit(1)
      .maybeSingle();
    const lastVisitDate = lastVisitRow?.booking_date ?? null;

    // この顧客が未担当枝客かどうかを判定（自分の担当顧客が紹介した顧客で、かつ自分は担当していない）
    let isUnassignedBranchCustomer = false;
    if (hostessId) {
      // 自分が担当しているかチェック
      const iAmAssigned = (hostessCustomers || []).some(
        (hc) => (hc as { hostess_id: string }).hostess_id === hostessId
      );

      if (!iAmAssigned) {
        // 自分の担当顧客が紹介しているかチェック
        const { data: myCustomerIds } = await supabase
          .from('hostess_customers')
          .select('customer_id')
          .eq('hostess_id', hostessId)
          .eq('relationship_type', 'assigned');

        if (myCustomerIds && myCustomerIds.length > 0) {
          const myCustomerIdSet = new Set(myCustomerIds.map(hc => hc.customer_id));
          // この顧客（id）を紹介した人が自分の担当顧客かチェック
          const isReferredByMyCustomer = (parentReferrals || []).some(
            (pr: ParentReferralRow) => pr.referrer?.id && myCustomerIdSet.has(pr.referrer.id)
          );
          isUnassignedBranchCustomer = isReferredByMyCustomer;
        }
      }
    }

    // 営業履歴を変換
    const announcements: Announcement[] = ((activities || []) as ActivityRow[]).map((a) => {
      const isMyActivity = hostessId ? a.hostess_id === hostessId : false;
      // 自分関連: 自分の活動 OR 未担当枝客への活動
      const isRelatedToMe = isMyActivity || isUnassignedBranchCustomer;

      return {
        id: a.id,
        date: formatDateTime(a.activity_date),
        activityDateIso: a.activity_date,
        store: a.store?.store_name || '不明',
        type: (ACTIVITY_TYPE_MAP[a.activity_type as keyof typeof ACTIVITY_TYPE_MAP] || 'メッセ') as ActivityType,
        hostess: {
          id: a.hostess?.id || '',
          name: a.hostess?.name || '不明',
          avatar: getHostessProfileImageUrlOrPlaceholder(a.hostess?.profile_image_path),
          status: a.hostess?.status === 'active' ? 'working' : 'absent',
        },
        isMyActivity,
        isRelatedToMe,
      };
    });

    // 予約履歴を変換
    const reservations: Reservation[] = ((bookings || []) as BookingRow[]).map((b) => {
      const bookingDate = new Date(b.booking_date);
      const isPast = bookingDate < new Date();
      const hasHostess = b.booking_hostesses?.some((bh) => bh.hostess_id && !bh.cancelled_at);
      let reservationType: ReservationType = '予約';
      if (hasHostess) {
        reservationType = '同伴';
      } else if (b.status === 'confirmed') {
        reservationType = '確約';
      }
      return {
        id: b.id,
        date: formatDate(b.booking_date),
        time: '', // 予約時刻カラムはデータベースに存在しない
        dayOfWeek: getDayOfWeek(b.booking_date),
        store: b.store?.store_name || '不明',
        type: reservationType,
        guestCount: String(b.reserved_guest_count || "") || "-",
        isPast,
      };
    });

    // 名刺を変換
    // 名刺画像の署名付きURL一括生成
    const bcRows = (businessCards || []) as BusinessCardRow[];
    const imagePaths = bcRows.flatMap((bc) => [bc.file, bc.file_back].filter(Boolean) as string[]);
    const signedUrlMap = new Map<string, string>();
    if (imagePaths.length > 0) {
      const { data: signedUrls } = await supabase.storage
        .from('bizcard-images')
        .createSignedUrls(imagePaths, 3600);
      if (signedUrls) {
        for (const item of signedUrls) {
          if (item.signedUrl && item.path) {
            signedUrlMap.set(item.path, item.signedUrl);
          }
        }
      }
    }

    const businessCardsData: BusinessCard[] = bcRows.map((bc) => ({
      id: String(bc.id),
      name: bc.name_kanji || '',
      kana: bc.name_furigana || '',
      romaji: '',
      mobile: bc.mobile_number || '',
      email: bc.email_address || '',
      companyCategory: '',
      companyName: bc.company_name || '',
      position: bc.position || '',
      companyPhone: bc.company_phone || '',
      companyFax: bc.fax || '',
      companyAddress: bc.address || '',
      postalCode: bc.postal_code ? normalizePostalCodeInput(bc.postal_code) : '',
      front: bc.file ? signedUrlMap.get(bc.file) : undefined,
      back: bc.file_back ? signedUrlMap.get(bc.file_back) : undefined,
      portrait: undefined,
      handwrittenMemo: bc.handwritten_note || '',
    }));

    // 顧客タイプに応じたアイコンとラベル
    const typeConfig: Record<string, { icon: string; label?: string }> = {
      customer: { icon: '🤵‍♂️' },
      branch: { icon: '🪾' },
      store: { icon: '🏛️' },
      'non-member': { icon: '👤', label: '非会員' },
      others: { icon: '🙅', label: '他の担当' },
    };
    const config = typeConfig[type] || typeConfig.customer;

    return {
      id: customer.id,
      type: type,
      name: customer.name || '',
      kana: customer.name_kana || '',
      romaji: '', // データベースに存在しないため空文字
      mobilePhone: customer.mobile_phone || undefined,
      email: customer.email || undefined,
      icon: config.icon,
      label: config.label,
      salesStatus: calculateSalesStatus(activityCounts),
      restrictions: {
        phone: customer.allow_phone === false,
        message: customer.allow_message === false,
        dm: customer.allow_dm === false,
        visit: customer.allow_visit === false,
      },
      stats,
      visitStatus: calculateVisitStatus(lastVisitDate),
      activityCounts,
      stores: Array.from(storeMap.values()),
      announcements,
      reservations,
      businessCards: businessCardsData,
      memos: {
        hallMemo: customer.memo || '',
        hostessMemo: memoData?.notes || '',
      },
      branches: type === 'assigned' ? branches : undefined,
    };
  } catch (error) {
    console.error('[fetchCustomerDetail] エラー:', error);
    throw error instanceof Error ? error : new Error('顧客詳細の取得に失敗しました');
  }
}

/**
 * ホステスメモを更新する
 * hostess_customer_memos テーブルに upsert で保存
 * 担当関係がなくてもメモを書ける
 */
export async function updateHostessMemo(
  customerId: string,
  hostessId: string,
  notes: string
): Promise<void> {
  const supabase = getBrowserClient();

  const { error } = await supabase
    .from('hostess_customer_memos')
    .upsert(
      { hostess_id: hostessId, customer_id: customerId, notes },
      { onConflict: 'hostess_id,customer_id' }
    );

  if (error) {
    const referenceError = CustomerReferenceError.fromPostgrestError(error);
    if (referenceError) throw referenceError;
    throw new Error(`メモの更新に失敗しました: ${error.message}`);
  }
}

/**
 * 枝客（ホステスの担当顧客が紹介した顧客）を取得する
 * @returns 枝客一覧（CustomerListItem形式）
 */
export async function fetchBranchCustomers(hostessId: string): Promise<CustomerListItem[]> {
  const supabase = getBrowserClient();

  try {
    // ホステスの担当顧客が紹介した枝客を取得
    // customer_referrals テーブルから referrer_id が担当顧客のレコードを取得
    const { data: referrals, error } = await supabase
      .from('customer_referrals')
      .select(`
        id,
        store_id,
        referred_id,
        created_at,
        referred:customers!referred_id(
          id,
          name,
          company_name,
          allow_phone,
          allow_message,
          allow_dm,
          allow_visit
        ),
        referrer:customers!referrer_id(
          id,
          name
        ),
        store:stores(
          id,
          store_name
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to fetch branch customers:', error);
      throw new Error('枝客一覧の取得に失敗しました');
    }

    if (!referrals || referrals.length === 0) {
      return [];
    }

    // 各枝客の来店回数を取得
    const visitCountsMap = new Map<string, number>();
    for (const referral of referrals as CustomerReferralRow[]) {
      if (referral.referred_id) {
        const { data: visitCount } = await supabase.rpc('get_customer_visit_count', {
          p_customer_id: referral.referred_id,
          p_hostess_id: hostessId,
          ...(referral.store_id ? { p_store_id: referral.store_id } : {}),
        });
        visitCountsMap.set(`${referral.referred_id}_${referral.store_id}`, visitCount || 0);
      }
    }

    // 枝客の最終来店日を一括取得
    const branchCustomerIds = (referrals as CustomerReferralRow[])
      .map(r => r.referred?.id || r.referred_id)
      .filter((id): id is string => !!id);

    // 会社名はホステス視点で代表名刺ベースに動的解決
    // 仕様: docs/domain/20260527-customer-company-context-display.md
    const branchCompanyMap = await resolveCustomerCompanies(supabase, branchCustomerIds, hostessId);

    const branchLastVisitMap = new Map<string, string>();
    if (branchCustomerIds.length > 0) {
      const { data: lastVisits } = await supabase
        .from('bookings')
        .select('customer_id, booking_date')
        .in('customer_id', branchCustomerIds)
        .eq('status', 'completed')
        .order('booking_date', { ascending: false });

      for (const row of lastVisits || []) {
        if (row.customer_id && row.booking_date && !branchLastVisitMap.has(row.customer_id)) {
          branchLastVisitMap.set(row.customer_id, row.booking_date);
        }
      }
    }

    // CustomerListItem形式に変換
    const branchCustomers: CustomerListItem[] = (referrals as CustomerReferralRow[]).map((referral) => {
      const customerId = referral.referred?.id || referral.id;
      return {
        id: customerId,
        type: 'branch' as DashboardCustomerType,
        registeredDate: new Date(referral.created_at).toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).replace(/\//g, '.'),
        lastVisitDate: formatDate(branchLastVisitMap.get(String(customerId)) || null),
        storeName: referral.store?.store_name || '不明',
        storeId: referral.store_id, // 営業活動記録用
        hostessName: '', // 紹介者の担当ホステスは別途取得が必要
        statusType: 'good' as const,
        statusLabel: '活動中',
        activities: {
          message: 0,
          phone: 0,
          note: 0,
          visit: visitCountsMap.get(`${referral.referred_id}_${referral.store_id}`) || 0,
        },
        restrictions: {
          phone: referral.referred?.allow_phone === false,
          message: referral.referred?.allow_message === false,
          dm: referral.referred?.allow_dm === false,
          visit: referral.referred?.allow_visit === false,
        },
        emoji: '🪾',
        name: referral.referred?.name || '不明',
        company: branchCompanyMap.get(String(customerId))?.companyName ?? '',
        linkTo: `/customers/${referral.referred?.id || referral.id}`,
      };
    });

    return branchCustomers;
  } catch (error) {
    console.error('Unexpected error fetching branch customers:', error);
    throw error instanceof Error ? error : new Error('枝客一覧の取得に失敗しました');
  }
}

/**
 * 同席顧客（担当以外でテーブルについたことがある顧客）を取得する
 * @param hostessId - ホステスID
 * @param storeId - 店舗ID
 * @returns 同席顧客一覧（CustomerListItem形式）
 */
export async function fetchAttendedCustomers(
  hostessId?: string,
  storeId?: string
): Promise<CustomerListItem[]> {
  if (!hostessId) return [];

  const supabase = getBrowserClient();

  try {
    // hostess_related_customers_summary ビューから同席顧客のみ取得
    const { data: relatedCustomers, error } = await supabase
      .from('hostess_related_customers_summary')
      .select(`
        customer_id,
        relation_type,
        last_relation_date,
        attend_count
      `)
      .eq('hostess_id', hostessId)
      .eq('relation_type', 'attended')
      .order('last_relation_date', { ascending: false });

    if (error) {
      console.error('Failed to fetch attended customers:', error);
      return [];
    }

    if (!relatedCustomers || relatedCustomers.length === 0) {
      return [];
    }

    // 顧客詳細を取得
    const customerIds = (relatedCustomers as RelatedCustomerRow[]).map((rc) => rc.customer_id);
    const { data: customers, error: customerError } = await supabase
      .from('customers')
      .select('id, name, company_name, registration_date, allow_phone, allow_message, allow_dm, allow_visit')
      .in('id', customerIds);

    if (customerError) {
      console.error('Failed to fetch customer details:', customerError);
      return [];
    }

    const customerMap = new Map(
      ((customers || []) as CustomerSimpleRow[]).map((c) => [c.id, c])
    );

    // バッチで営業活動統計を取得（N+1クエリ問題の解消）
    const activityStatsMap = await getBatchActivityStats(customerIds, { storeId, hostessId });

    // 会社名はホステス視点で代表名刺ベースに動的解決
    // 仕様: docs/domain/20260527-customer-company-context-display.md
    const companyMap = await resolveCustomerCompanies(supabase, customerIds, hostessId);

    // CustomerListItem形式に変換
    const attendedCustomers: CustomerListItem[] = [];
    for (const rc of relatedCustomers as RelatedCustomerRow[]) {
      const customer = customerMap.get(rc.customer_id);
      if (!customer) continue;

      const activityStats = activityStatsMap.get(rc.customer_id) || {
        phone: 0, message: 0, note: 0, visit: 0,
        statusType: 'poor' as const, statusLabel: '営業不足',
      };

      const formattedDate = rc.last_relation_date
        ? new Date(rc.last_relation_date).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).replace(/\//g, '.')
        : '-';
      attendedCustomers.push({
        id: rc.customer_id,
        type: 'assigned' as DashboardCustomerType, // 同席顧客は「他」カテゴリとして表示
        registeredDate: formattedDate,
        lastVisitDate: formattedDate,
        storeName: '',
        storeId: storeId,
        statusType: activityStats.statusType,
        statusLabel: `同席${rc.attend_count}回`,
        activities: {
          phone: activityStats.phone,
          message: activityStats.message,
          note: activityStats.note,
          visit: rc.attend_count || 0,
        },
        restrictions: {
          phone: customer.allow_phone === false,
          message: customer.allow_message === false,
          dm: customer.allow_dm === false,
          visit: customer.allow_visit === false,
        },
        emoji: '🪑', // 同席顧客用の絵文字
        name: customer.name || '不明',
        company: companyMap.get(rc.customer_id)?.companyName ?? '',
        linkTo: `/customers/${rc.customer_id}`,
        relationInfo: {
          type: 'attended',
          attendCount: rc.attend_count ?? undefined,
          lastAttendDate: rc.last_relation_date ?? undefined,
        },
      });
    }

    return attendedCustomers;
  } catch (error) {
    console.error('Unexpected error fetching attended customers:', error);
    return [];
  }
}

/**
 * 顧客のNG設定（allow_phone / allow_message）を更新
 */
export async function updateCustomerRestrictions(
  customerId: string,
  restrictions: { allowPhone: boolean; allowMessage: boolean; allowDm: boolean; allowVisit: boolean },
): Promise<void> {
  const supabase = getBrowserClient();

  const { error } = await supabase
    .from('customers')
    .update({
      allow_phone: restrictions.allowPhone,
      allow_message: restrictions.allowMessage,
      allow_dm: restrictions.allowDm,
      allow_visit: restrictions.allowVisit,
    })
    .eq('id', customerId);

  if (error) {
    throw new Error(`NG設定の更新に失敗しました: ${error.message}`);
  }
}

export async function updateCustomerName(
  customerId: string,
  name: string,
  nameKana: string,
): Promise<void> {
  const supabase = getBrowserClient();

  const normalizedName = normalizeName(name);
  const normalizedKana = normalizeName(nameKana);

  // updated_by / updated_at は customers の update_customers_updated_at BEFORE トリガが
  // auth.uid() / NOW() で自動セットするためアプリ側では渡さない (渡しても上書きされる)。
  const { error } = await supabase
    .from('customers')
    .update({
      name: normalizedName,
      name_kana: normalizedKana || null,
    })
    .eq('id', customerId);

  if (error) {
    throw new Error(`氏名の更新に失敗しました: ${error.message}`);
  }
}
